import {
  Component,
  EventEmitter,
  Inject,
  Input,
  ViewChild,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import * as Redux from 'redux';
import { AppState } from '@iv/store/app.reducer';
import { AppStore } from '@iv/store/app.store';
import { getCurrentTab } from '@iv/store/tabs/tabs.selectors';
import * as TabsActions from '@iv/store/tabs/tabs.actions';
import { Instrument } from '@iv/model/instrument';
import { OptionMonitorService } from '../../option-monitor.service';
import { map } from 'rxjs/operators';
import { TabTypeEnum } from '@iv/model/tabs.model';

@Component({
  selector: 'iv-live-option-monitor-filters',
  templateUrl: './option-monitor-filters.component.html',
  styleUrls: ['./option-monitor-filters.component.scss']
})
export class OptionMonitorFiltersComponent implements OnInit, OnChanges, OnDestroy {

  constructor(
    private optionMonitorService: OptionMonitorService,
    @Inject(AppStore) private store: Redux.Store<AppState>
  ) {
    this.unsubscribeStore = store.subscribe(() => this.updateState());
    this.updateState();
  }

  public set expirations(value: any) {
    this.updateFilterValue('expirations', value);
    this.updateFilterState.expirations = true;
  }

  public get expirations(): any {
    return this._expirations;
  }

  get strikesN(): any {
    return this._strikesN;
  }

  set strikesN(value: any) {
    this.updateFilterValue('strikesN', value);
    this.updateFilterState.strikesN = true;
  }

  get type(): any {
    return this._type;
  }

  set type(value: any) {
    this.updateFilterValue('type', value);
    this.updateFilterState.type = true;

  }

  get center(): number {
    return this._center;
  }

  set center(value: number) {
    this.updateFilterValue('center', value);
    this.updateFilterState.center = true;
  }

  @Input()
  public userId: number;
  @Input()
  public expirationsLoaded: { flag: boolean, exp: any[] };
  @ViewChild('expirationsDropdown')
  private expirationsDropdown;

  @Output()
  public emitSubmit = new EventEmitter<any>();
  @Output()
  public downloadCSVClick = new EventEmitter<any>();
  @Output()
  public expirationSelected = new EventEmitter<any>();

  public instrument: Instrument;
  public expirationList: any[] = [];
  public strikes: String[] = ['1', '2', '5', 'All'];
  public types: any[] = [{name: 'Call&Put', value: ''}, {name: 'Call', value: 'CALL'}, {name: 'Put', value: 'PUT'}];
  public currentTab = getCurrentTab(this.store.getState());
  public id = this.currentTab.type === TabTypeEnum.OPTION_CHAIN ? this.currentTab.data.instrument.stockId : this.currentTab.data.future.underlyingId;

  private unsubscribeStore;
  private updateFilterState: any = {};
  private tabType: string;
  private _expirations: any = null;
  private _strikesN: any = null;
  private _type: any = null;
  private _center: number = null;

  private static _getDefaultExpiration() {
    return {
      id: 'ALL',
      value: 'All',
      type: 'type'
    };
  }

  private _host = 'https://www.ivolatility.com';

  public get host() {
    return this._host;
  }

  public ngOnInit(): void {
    this.updateFilterState = {};
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.expirationsLoaded.currentValue.flag) {
      this.loadFilters();
    }
  }

  public ngOnDestroy(): void {
    if (typeof this.unsubscribeStore === 'function') {
      this.unsubscribeStore();
    }
  }

  updateFilterValue(name, value) {
    this.expirationSelected.emit(value);
    const prev = this['_' + name];
    if (prev !== value) {
      this['_' + name] = value;
    }
  }

  updateState() {
    const state = this.store.getState();
    const currentTab = getCurrentTab(state);
    if (currentTab) {
      const data = currentTab.data;
      this.tabType = currentTab.type;
      const serviceTabs = data.serviceTabs;
      if (serviceTabs) {
        const filters = serviceTabs[0].filters;
        this.instrument = data.instrument;
        this.expirations = filters.expirations;
        this.strikesN = filters.strikesN;
        this.center = filters.center;
        this.type = filters.type;
      }
    }
  }

  getClientWidth() {
    return document.documentElement.clientWidth;
  }

  submit() {
    if (this.updateFilterState.expirations) {
      this.changeFilter('expirations', this.expirations);
    }
    if (this.updateFilterState.strikesN) {
      this.changeFilter('strikesN', this.strikesN);
    }
    if (this.updateFilterState.type) {
      this.changeFilter('type', this.type);
    }
    if (this.updateFilterState.center) {
      this.changeFilter('center', this.center);
    }
    this.updateFilterState = {};
    this.emitSubmit.emit();
  }

  onDownloadCSVClick(event) {
    this.downloadCSVClick.emit(event);
  }

  private loadFilters(): void {
    this.expirationList = this.expirationsLoaded.exp;
    const method = this.tabType === 'instrument' ? 'getFilterTypes' : 'getFutureFilterTypes';
    let filteredTypes = [];

    this.optionMonitorService[method]()
      .pipe(map(data => data.body ? data.body : data))
      .subscribe(data => {
        // todo переделать (реализовать набор экспираций для стока на бэке)
        if (this.id === 18155) {
          filteredTypes.push({
            id: 'ALL',
            value: 'All',
            type: 'type'
          }, {
            id: 'WEEKLYS',
            value: 'Weekly\'s',
            type: 'type'
          }, {
            id: 'VIX_RULE',
            value: 'VIX rule (3rd Wednessday)',
            type: 'type'
          });
        } else {
          for (const i in data) {
            filteredTypes.push({
              id: i,
              value: data[i],
              type: 'type'
            });
          }
        }
        filteredTypes = filteredTypes.sort(function (a, b) {
          if (a.id.substring(0, 1) > b.id.substring(0, 1)) {
            return 1;
          }

          if (a.id.substring(0, 1) < b.id.substring(0, 1)) {
            return -1;
          }
          return 0;
        });
        this.expirationList = [...filteredTypes.sort((filter) => filter), ...this.expirationList];
        if (this.tabType === 'future') {
          this.expirations = OptionMonitorFiltersComponent._getDefaultExpiration();
          return;
        }
        this.expirations = this._initExpirations();
      });
  }

  private _initExpirations() {
    if (Array.isArray(this.expirations)) {
      const expirations = [];
      for (let i = 0; i < this.expirations.length; i++) {
        if (this._isExpirationExist(this.expirations[i])) {
          expirations.push(this.expirations[i]);
        }
      }
      if (expirations.length > 0) {
        return expirations;
      }
      return OptionMonitorFiltersComponent._getDefaultExpiration();
    } else {
      if (!this._isExpirationExist(this.expirations)) {
        return OptionMonitorFiltersComponent._getDefaultExpiration();
      } else {
        return this.expirations;
      }
    }
  }

  private _isExpirationExist(expiration): boolean {
    return this.expirationList.some(e => {
      return e.value === expiration.value;
    }, this);
  }

  public changeFilter(name, value) {
    if (!Array.isArray(value) && value.type !== 'expiration') {
      this.expirationsDropdown.showDropdown = false;
    }
    this.store.dispatch(TabsActions.updateServiceTabData({
      name: name,
      value: value
    }));
    this.emitSubmit.emit();
  }

  viewFromSlider(exp) {
    this.store.dispatch(TabsActions.updateServiceTabData({
      name: 'expirations',
      value: exp
    }));
    this.expirations = exp;
    this.emitSubmit.emit();
  }
}
